/**
 * PRISM Graphics' release notes, shown in the editor (the version label opens them). Newest first. Each release
 * says, in the words people see in the editor, what it is in brief (`summary`), what it added (`features`) and what
 * it fixed (`fixes`). The full technical history is CHANGELOG.md; this is the part editor users and administrators
 * read. English only, like the changelog.
 */

export interface Release {
  version: string;
  /** ISO date, YYYY-MM-DD. */
  date: string;
  title: string;
  /**
   * The release in brief: 1–6 bullets, each at most 20 words, in the words people see in the editor. No setting
   * (environment variable) names, file paths, error codes or decision record numbers; those belong in the details
   * or the changelog. Checked by `summaryProblems`.
   */
  summary: string[];
  features: string[];
  fixes: string[];
}

export const SUMMARY_MIN_BULLETS = 1;
export const SUMMARY_MAX_BULLETS = 6;
export const SUMMARY_MAX_WORDS = 20;

/** What breaks the summary rules for one release, in plain words; empty when it follows them. */
export function summaryProblems(release: Release): string[] {
  const problems: string[] = [];
  const v = release.version;
  const n = release.summary.length;
  if (n < SUMMARY_MIN_BULLETS || n > SUMMARY_MAX_BULLETS) problems.push(`${v} summary has ${n} bullets (${SUMMARY_MIN_BULLETS}–${SUMMARY_MAX_BULLETS})`);
  release.summary.forEach((b, i) => {
    const where = `${v} summary[${i}]`;
    const words = b.trim().split(/\s+/).filter(Boolean).length;
    if (words === 0) problems.push(`${where} is empty`);
    if (words > SUMMARY_MAX_WORDS) problems.push(`${where} has ${words} words (at most ${SUMMARY_MAX_WORDS})`);
    // GRAPHICS_PACKAGE_MAX_BYTES, PACKAGE_TOO_LARGE: settings and error codes.
    if (/\b[A-Z][A-Z0-9]*_[A-Z0-9_]+\b/.test(b)) problems.push(`${where} names a setting or an error code`);
    if (/\b[1-5]\d\d\b/.test(b) && /\b(HTTP|status|error)\b/i.test(b)) problems.push(`${where} names an HTTP status`);
    if (/\bADRs?\b/.test(b)) problems.push(`${where} names a decision record`);
    if (/`/.test(b)) problems.push(`${where} has code formatting`);
    if (/(^|[\s(`'"])\.{0,2}\/[\w.-]+\/[\w./-]*/.test(b) || /(^|[\s(`'"])[\w.-]+\/[\w.-]+\.\w+\b/.test(b)) problems.push(`${where} has a file path`);
    if (/\b[\w-]+\.(ts|tsx|mjs|js|json|sh|yaml|md)\b/.test(b)) problems.push(`${where} names a file`);
  });
  return problems;
}

export const RELEASES: Release[] = [
  {
    version: '1.2.0',
    date: '2026-10-28',
    title: 'One graphic, a layout per shape',
    summary: [
      'A graphic can have layouts for 16:9, 9:16, 4:5 and 1:1 in one package',
      'PRISM shows the layout that matches each sequence format of a multi-format edit',
      'Position, size, font size and visibility can differ per layout; everything else is shared',
      'Every starter template now has all four layouts',
    ],
    features: [
      'Layout tabs above the canvas: switch layouts, add one with “+”, change its format or remove it from its menu. Each tab shows how many problems its layout has.',
      'In a layout other than the primary, moving, resizing, changing the font size or hiding an element changes that layout only. The inspector marks what differs and offers “Reset to primary”.',
      'Text, colours, new elements, deletions and timing apply to every layout. A keyframe whose value differs in a layout is outlined on the timeline.',
      'New graphics and templates can start with several layouts (“Layouts” on the start screen). Inside PRISM, a multi-format sequence preselects its formats.',
      'The preview has a layout select and loads the graphic at that layout’s size.',
    ],
    fixes: [],
  },
  {
    version: '1.1.1',
    date: '2026-10-18',
    title: 'Full-screen title warning fixed',
    summary: ['A one-line headline in the Full-screen title is no longer reported as having too many lines'],
    features: [],
    fixes: [
      'The Problems panel no longer says a tightly spaced headline (like the Full-screen title’s) has more lines than allowed when it fits. Text that really is cut is still listed.',
    ],
  },
  {
    version: '1.1.0',
    date: '2026-10-18',
    title: 'Vertical, portrait and square graphics',
    summary: [
      'New formats as in PRISM: vertical 9:16, portrait 4:5 and square 1:1 beside 16:9',
      'Each format offers only the frame rates PRISM allows for it',
      'Change format… turns a graphic into another shape, for example a duplicated 16:9 lower third into 9:16',
      'Every starter template now comes in each shape',
    ],
    features: [
      'The format list in the top bar, on the start screen and in the template picker inside PRISM is grouped by shape: landscape 16:9 (HD, UHD, 720), vertical 9:16 (1080 × 1920), portrait 4:5 (1080 × 1350) and square 1:1 (1080 × 1080).',
      'The frame-rate list shows the rates PRISM allows for the chosen format (25, 29.97 and 30 for vertical, portrait and square). A format without the current rate switches to its first one; Undo takes it back.',
      'Change format… (in the format list, or Design → Canvas) scales the graphic to fit the new shape and keeps each element where its anchor says: at its edge, centred or stretched. A preview shows the result; the Problems panel lists anything that left the safe area.',
      'Starter templates in 9:16, 4:5 and 1:1, tuned for each shape: on vertical, lower thirds sit higher, clear of a phone app’s buttons. The picker shows the chosen shape, or all of them.',
      'Inside PRISM, a new graphic starts in the format PRISM suggests, and you can pick another one above the templates.',
      'Thumbnails follow the graphic’s shape (a vertical graphic gets a tall thumbnail).',
      'The 4:3 centre cut is offered for 16:9 graphics only. A canvas size PRISM cannot use (odd, too small or too large) is listed in the Problems panel.',
    ],
    fixes: ['A graphic opened read-only in PRISM plays at its own size and frame rate.'],
  },
  {
    version: '1.0.0',
    date: '2026-10-18',
    title: 'First official release',
    summary: [
      'PRISM Graphics designs broadcast graphics inside PRISM: text, shapes, images and video, animated on one timeline',
      'Fields let operators fill in names and titles; Preview and Check show exactly how PRISM will play them',
      'Save to PRISM adds a graphic, a new version or a copy; PRISM renders it frame-exactly',
      'New: click the version to read these release notes',
    ],
    features: [
      'Release notes in the editor: click the version at the right of the top bar or at the foot of the start screen.',
      'Otherwise the same as release candidate 3: the graphics, the saved packages and the sample packages are unchanged.',
      'Works with PRISM 1.0.0.',
    ],
    fixes: [],
  },
  {
    version: '1.0.0-rc.3',
    date: '2026-10-18',
    title: '1.0 release candidate 3, fixes from the review',
    summary: [
      'The version is shown at the right of the top bar and at the foot of the start screen',
      'Fixed: saving to PRISM no longer overwrites edits made while the save runs',
      'Fixed: leaving the editor saves your last edits; no shortcut runs behind an open dialog',
      'Fixed: an expired PRISM session makes the editor read-only until you are signed in again',
      'Fixed: overshooting colour animations, video timing and crossfades play correctly in long sessions',
    ],
    features: [
      'The editor’s version is shown as a small label at the right of the top bar (hidden when the window is narrow) and at the foot of the start screen.',
      'Administrators can cap the size of a graphic’s package (1 GiB by default) and how many exports, checks and saves are built at once (2 by default); larger packages are refused before anything is read.',
      'Large video, images and fonts that are already compressed are stored in the package as they are, so exports are faster.',
    ],
    fixes: [
      'Saving to PRISM no longer overwrites edits made while it runs (an autosave, another tab): the newer graphic is kept, and the editor reloads it when you have not edited since the save started.',
      'Undo after “Save as copy” keeps the copy’s name and identity.',
      'Leaving the editor saves pending edits one last time, also for large graphics.',
      'No editor shortcut runs behind a dialog.',
      'Pasted keyframes are checked before they are applied.',
      'When the PRISM session expires, the editor becomes read-only (no edits, autosave paused) until PRISM signs you in again.',
      'Colours animated with an overshooting easing (back, bezier) stay valid colours.',
      'A video keeps its timing after long real-time playback; a video that cannot play within 15 seconds now fails to load instead of hanging.',
      'The crossfade of an update looks the same whichever frame PRISM renders first.',
      'Long sessions in PRISM stay fast and use bounded memory while any moment can still be shown.',
      'Loading a graphic again, or closing it, while it was still loading no longer leaves fonts, videos or page elements behind.',
      'SVG files with very deep nesting are refused, and checking SVG files is faster.',
      'A failed check is retried the next time instead of failing again.',
      'Uploads to PRISM that stall or report a wrong size fail cleanly; a connection lost while reading PRISM’s answer is retried.',
      'Closing the editor from PRISM no longer waits forever, and reloading the editor ends a save or close PRISM was waiting for (bridge 1.0.1, optional for PRISM).',
    ],
  },
  {
    version: '1.0.0-rc.2',
    date: '2026-10-10',
    title: '1.0 release candidate 2, readable number fields',
    summary: ['Fixed: number-field labels no longer overlap their values in narrow columns such as the safe-area settings'],
    features: [],
    fixes: [
      'Number-field labels keep their width and the input shrinks instead, so narrow columns stay readable.',
      'The safe-area fields in the Developer settings show short labels (“Action”, “Graphics”), with the full name as tooltip.',
    ],
  },
  {
    version: '1.0.0-rc.1',
    date: '2026-10-09',
    title: '1.0 release candidate 1, the complete editor',
    summary: [
      'Design graphics on a canvas that renders exactly like PRISM, with layers, fields, brand kit and starter templates',
      'Animate in, steps, updates and out on one timeline, with easing presets and one-click animation presets',
      'Preview and Check play the exported graphic as PRISM does, frame by frame on its timeline',
      'Save to PRISM as a new graphic, a new version or a copy; open saved graphics again from PRISM',
      'Keyboard-accessible panels and dialogs in dark and light themes; faster editing of large graphics',
      'The out animation now ends exactly on the clip’s last frame, which is transparent',
    ],
    features: [
      'Canvas built on the graphic runtime (what you see is what renders), with rulers, guides, snapping, safe areas and a 4:3 cut.',
      'Text, shapes, images, SVG and video (WebM with alpha, MP4); auto-size boxes and stacks, anchoring, clip masks, gradient text, rotation-aware resizing.',
      'Fields and the Data panel with live test values; warnings for overflow, blend modes over transparency, large blur, video codec and package size.',
      'Timeline of phases (in, steps, hold loops, update with per-field transitions, out, custom actions and stings), keyframes with easing presets and a bezier editor, colour tracks.',
      'Animation presets that stay editable: fade, slide, wipe, reveal, scale pop, per-line and per-character stagger, typewriter.',
      'Brand kit for the workspace in the Brand panel; five starter templates (lower third, name strap, locator, full-screen title, logo bug).',
      'Undo and redo, autosave, copy and paste between graphics, keyboard shortcuts (press ? to see them).',
      'Preview panel: the exported graphic in real time, or on a PRISM-like timeline, with a log of calls.',
      'Check: validates the package, plays it once and makes its thumbnail.',
      'Save to PRISM: a new graphic, a new version, a copy or an overwrite, with a prompt when someone else saved in between. Reopening a PRISM version reuses your draft.',
      'Accessibility: every panel and dialog can be used with the keyboard; dialogs keep focus; keyframes can be selected and moved from the keyboard.',
      'Editing a graphic with many elements is about twice as fast.',
      'For administrators: the editor runs beside PRISM on the same host, with rate limits, trusted proxies and S3 object storage configurable.',
    ],
    fixes: [
      'The out animation completes on the clip’s last frame: that frame is now fully transparent instead of showing a trace of the out, and the hold is one frame shorter.',
      'A video frame that does not appear in PRISM no longer hangs the render; it is sought again after a second and fails after five.',
      'Contrast of text on accent colours, field labels and light-theme status colours, and a stronger focus ring.',
      'Enter and Space activate focused buttons again.',
      'Uploaded SVG files, fonts and packages are checked more strictly; damaged fonts are refused with a clear message.',
    ],
  },
];
